import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AppRole } from '@/lib/rolePermissions';
import { useAuthContext } from '@/contexts/AuthContext';

export interface TeamMember {
  id: string;
  email: string;
  full_name: string | null;
  position: string | null;
  avatar_url: string | null;
  profile_color: string | null;
  show_avatar: boolean;
  role: AppRole | null;
  role_id: string | null;
  created_at: string;
  department: string | null;
  phone: string | null;
  location: string | null;
  hire_date: string | null;
  salary_monthly: number | null;
  salary_notes: string | null;
  employee_notes: string | null;
  contract_type: string | null;
  vacation_days_year: number | null;
  vacation_days_remaining: number | null;
}

export const fetchMembersFromDb = async (): Promise<TeamMember[]> => {
  const [profilesResult, rolesResult] = await Promise.all([
    supabase
      .from('profiles')
      .select('id, full_name, position, avatar_url, profile_color, show_avatar, created_at, email, department, phone, location, hire_date, salary_monthly, salary_notes, employee_notes, contract_type, vacation_days_year, vacation_days_remaining'),
    supabase
      .from('user_roles')
      .select('id, user_id, role'),
  ]);

  if (profilesResult.error) throw profilesResult.error;
  if (rolesResult.error) throw rolesResult.error;

  return (profilesResult.data || []).map((profile: any) => {
    const userRole = rolesResult.data?.find((r) => r.user_id === profile.id);
    return {
      id: profile.id,
      email: profile.email || '',
      full_name: profile.full_name,
      position: profile.position,
      avatar_url: profile.show_avatar !== false ? profile.avatar_url : null,
      profile_color: profile.profile_color,
      show_avatar: profile.show_avatar !== false,
      role: (userRole?.role as AppRole) || null,
      role_id: userRole?.id || null,
      created_at: profile.created_at,
      department: profile.department || null,
      phone: profile.phone || null,
      location: profile.location || null,
      hire_date: profile.hire_date || null,
      salary_monthly: profile.salary_monthly || null,
      salary_notes: profile.salary_notes || null,
      employee_notes: profile.employee_notes || null,
      contract_type: profile.contract_type || null,
      vacation_days_year: profile.vacation_days_year || null,
      vacation_days_remaining: profile.vacation_days_remaining || null,
    };
  });
};

export const useTeamMembers = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuthContext();

  const { data: members = [], isLoading: loading, refetch } = useQuery({
    queryKey: ['team-members'],
    queryFn: fetchMembersFromDb,
    enabled: !!user && !authLoading,
  });

  const assignRole = async (userId: string, role: AppRole) => {
    try {
      const member = members.find((m) => m.id === userId);

      if (member?.role_id) {
        const { error } = await supabase
          .from('user_roles')
          .update({ role })
          .eq('id', member.role_id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_roles')
          .insert({ user_id: userId, role });
        if (error) throw error;
      }

      toast({ title: 'Rolle zugewiesen', description: 'Die Rolle wurde erfolgreich aktualisiert.' });
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
    } catch (error: any) {
      console.error('Error assigning role:', error);
      toast({ variant: 'destructive', title: 'Fehler', description: 'Rolle konnte nicht zugewiesen werden.' });
      throw error;
    }
  };

  const removeRole = async (userId: string) => {
    try {
      const member = members.find((m) => m.id === userId);
      if (!member?.role_id) return;

      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('id', member.role_id);
      if (error) throw error;

      toast({ title: 'Rolle entfernt', description: 'Die Rolle wurde erfolgreich entfernt.' });
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
    } catch (error: any) {
      console.error('Error removing role:', error);
      toast({ variant: 'destructive', title: 'Fehler', description: 'Rolle konnte nicht entfernt werden.' });
      throw error;
    }
  };

  return {
    members,
    loading,
    fetchMembers: refetch,
    assignRole,
    removeRole,
  };
};
